import { beautify, createViewAction, formatDate } from "../../utils/helpers";
import { FormActions } from "../../components/common/FormActions";
import { AGENT, APPROVED, MERCHANT, REJECTED } from "../../utils/constants";
import useSet from "../common/useSet";
import {
  API_APPROVE_AGENTS,
  API_APPROVE_MERCHANTS,
  API_APPROVE_STAFF,
  API_REJECT_USER,
} from "../../utils/apis";
import { useEffect } from "react";
import { Button, message } from "antd";
import { URL_APPLICATION_USERS } from "../../utils/urls";
import { useGlobalContext } from "../../context/GlobalContext";
import { StatusTag } from "../../components/common/registerUtils";
import { useToasts } from "../common/useToasts";
import { useAuth } from "../../context/AuthContext";

export const useApplicationUsers = () => {
  const { success, error } = useSet();
  const { updateData } = useGlobalContext();

  const handleViewAttachment = (url: string) => {
    if (url) {
      updateData({ openAttachment: true, selectedAttachment: url });
    } else {
      console.warn("No attachment URL provided");
    }
  };

  const tableColumns = () => [
    {
      id: "application_number",
      header: "Application Number",
      filter: {},
    },
    {
      id: "first_name",
      header: "First Name",
      filter: {
        type: "text",
      },
    },
    {
      id: "last_name",
      header: "Last Name",
      filter: {
        type: "text",
      },
    },
    {
      id: "email",
      header: "Email",
      filter: {
        type: "text",
      },
    },
    {
      id: "phone_number",
      header: "Phone Number",
      filter: {
        type: "text",
      },
    },
    {
      id: "role",
      header: "Role",
      filter: {
        type: "text",
        key: "role_name",
      },
    },
    {
      id: "actions",
      header: "Actions",
    },
  ];

  useEffect(() => {
    if (success) {
      message.success(`Updated user status`);
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    }

    if (Object?.keys(error)?.length > 0) {
      message.error(error?.message || "Failed to update status");
    }
  }, [success, error]);

  const tableData = (data: any) => [
    ...data?.map((item: any) => {
      const row = {
        ...item,
        phone_number: item?.application_data?.phone_number,
        first_name:
          item?.application_data?.first_name ||
          (item.application_data.taxpayer_name.split(" ").length === 3
            ? `${item.application_data.taxpayer_name.split(" ")[0]} ${item.application_data.taxpayer_name.split(" ")[1]}`
            : item.application_data.taxpayer_name.split(" ")[0]),

        last_name:
          item?.application_data?.last_name ||
          (item.application_data.taxpayer_name.split(" ").length === 3
            ? item.application_data.taxpayer_name.split(" ")[2]
            : item.application_data.taxpayer_name.split(" ")[1]),
        date_joined: formatDate(item.date_joined),
        email: item?.application_data.email,
        status: <StatusTag status={item?.status} />,
        role: beautify(item?.role_name),
        actions: (
          <FormActions
            actions={[createViewAction(URL_APPLICATION_USERS, item?.id)]}
          />
        ),
      };
      return row;
    }),
  ];

  const viewData = (item: any) => {
    // Normalize role_name
    const normalizedRole = item?.role_name?.toUpperCase();
    const isMerchant = normalizedRole === MERCHANT;
    const isAgent = normalizedRole === AGENT;
    const isStaff = !isAgent && !isMerchant;

    return {
      sections: [
        {
          title: isMerchant
            ? "Merchant Details"
            : isAgent
              ? "Agent Details"
              : "User Details",
          fields: {
            ...(isMerchant && {
              id: item?.id,
              business_name: item?.application_data?.business_name,
              business_email: item?.application_data?.email,
              taxpayer_name: item?.application_data?.taxpayer_name,
              phone_number: item?.application_data?.phone_number,
              business_industry: item?.application_data?.industry,
              business_classification:
                item?.application_data?.business_classification,
            }),

            ...(isAgent && {
              id: item?.id,
              first_name: item?.application_data?.first_name,
              last_name: item?.application_data?.last_name,
              other_name: item?.application_data?.other_name,
              email: item?.application_data?.email,
              phone_number: item?.application_data?.phone_number,
              ownership_type: item?.application_data?.ownership_type,
              date_applied: formatDate(item?.date_joined),
            }),

            ...(isStaff && {
              id: item?.id,
              first_name: item?.application_data?.first_name,
              last_name: item?.application_data?.last_name,
              email: item?.application_data?.email,
              phone_number: item?.application_data?.phone_number,
              role: item?.role_name,
            }),
          },
        },

        {
          title: isMerchant
            ? "Settlement Details"
            : isAgent
              ? "Profile Details"
              : "Additional Details",
          fields: {
            ...(isAgent && {
              nationality: item?.application_data?.nationality,
              nin: item?.application_data?.nin,
            }),

            ...(isMerchant && {
              tin: item?.application_data?.tin,
              brn: item?.application_data?.brn,
              ...(item?.application_data?.preferred_option !== "TELECOM"
                ? {
                    bank_name: item?.application_data?.bank_name,
                    bank_branch: item?.application_data?.bank_branch,
                    bank_account: item?.application_data?.account_number,
                  }
                : {
                    telecom_mobile_number:
                      item?.application_data?.telecom_mobile_number,
                  }),
            }),

            ...(isStaff && {
              // Staff users may not have extra details, but include placeholders
              created_at: formatDate(item?.created_at),
              status: <StatusTag status={item?.status} />,
            }),
          },
        },

        ...(item?.attachments?.length > 0
          ? [
              {
                title: "Documents",
                fields: {
                  attachments: (
                    <div
                      style={{
                        display: "flex",
                        flexWrap: "wrap",
                        flexDirection: "column",
                        gap: "8px",
                      }}
                    >
                      {item.attachments.map((att: any, index: number) => (
                        <Button
                          key={index}
                          type="primary"
                          style={{
                            marginBottom: 4,
                            width: "fit-content",
                            paddingLeft: 10,
                            paddingRight: 10,
                          }}
                          size="large"
                          onClick={() => handleViewAttachment(att.file)}
                        >
                          View {att.caption}
                        </Button>
                      ))}
                    </div>
                  ),
                },
              },
            ]
          : []),
      ],
    };
  };

  const formRoute = "/users/create";
  const tableTitle = "Pending Users";
  const createTitle = "New User";
  return {
    tableColumns,
    tableData,
    viewData,
    status: "pending",
    createTitle,
    tableTitle,
    formRoute,
  };
};

export const useApplicationUserActions = ({ form, data }: any) => {
  const { user } = useAuth();
  const {
    uploadData,
    success: approveSuccess,
    error: approveError,
    submitting,
  } = useSet();

  const {
    uploadData: uploadRejectData,
    success: rejectSuccess,
    error: rejectError,
    submitting: rejecting,
  } = useSet();

  useToasts({
    success: approveSuccess,
    error: approveError,
    submitting: submitting,
    redirect: "/register/application_users",
    successMessage: "User approved successfully",
  });

  useToasts({
    success: rejectSuccess,
    error: rejectError,
    submitting: rejecting,
    redirect: "/register/application_users",
    successMessage: "User rejected successfully",
  });

  // Determine if form should be shown
  const showActionForm = data?.created_by?.id !== user?.id;

  const fields = [
    ...(showActionForm
      ? [
          {
            field_name: "action",
            label: "Action",
            type: "select",
            options_data: [
              { name: "Approve", id: APPROVED },
              { name: "Reject", id: REJECTED },
            ],
            props: { required: true },
          },
          {
            field_name: "comment",
            label: "Comment",
            type: "textarea",
            props: { required: true },
          },
        ]
      : []),
  ];

  function onSubmit(payload: any) {
    if (!data) return null;
    if (payload.action === APPROVED) {
      return uploadData({
        api:
          beautify(data?.role_name) === "Agent"
            ? API_APPROVE_AGENTS
            : beautify(data?.role_name) === "Merchant"
              ? API_APPROVE_MERCHANTS
              : API_APPROVE_STAFF,
        params: { application_id: data?.id, reason: payload?.comment },
      });
    } else if (payload.action === REJECTED) {
      return uploadRejectData({
        api: API_REJECT_USER,
        method: "POST",
        params: { application_id: data.id, reason: payload.comment },
      });
    }
  }

  const enableSubmit = !!form?.action && !!form?.comment;

  return {
    fields,
    onSubmit,
    enableSubmit,
    showActionForm,
    finalStates: [APPROVED, REJECTED],
  };
};
